import { simulate } from 'api/simulation/simulateApi';
import { put, takeLatest } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { withRetry } from 'store/sagaUtils';
import type { SimulationRequestInterface } from 'store/simulation/types';
import { cleanUpRequestParams } from 'store/simulation/cleanSimulationRequest';
import type { PGSimulationRequestActionInterface } from './types';
import { SIMULATE_MIN_MAX } from './types';

import {
  setMinSimulationResult,
  setMaxSimulationResult,
} from 'store/performanceGuarantee/reducer';

import { convertBulkDensity } from 'util/conversions';
import type { UnitInterface } from 'store/units/types';
import { UnitTypes } from 'unitData/types';

type PromiseReturnType<T> =
  T extends Promise<infer U>
    ? U
    : T extends (...args: never) => Promise<infer UFunc>
      ? UFunc
      : never;

const getFeedUnit = (units: UnitInterface[]) => {
  const feedUnits = units.filter((u) => u.type === UnitTypes.FEED);

  if (feedUnits.length !== 1) {
    // eslint-disable-next-line no-console
    console.log('Only 1 feed should exist on the flowsheet');
    return false;
  }

  return feedUnits[0];
};

const getRequestParams = (
  cleanedUpRequestParams: SimulationRequestInterface,
  specificGravityPercentVariance: number,
  workIndexNumberVariance: number,
  feedSizeDistributionPercentVariance: number,
) => {
  // Since we're changing Wi and Specific Gravity, that is set on the feed
  // The logical way to do that would be - look through all of the elements on the canvas, and
  // select only feeds. If != 1 - we don't know how to handle 0 or 2 feeds - write an error about it
  // If it's only 1 feed - take that feed ID or something, so we could map it later here and change the
  // Wi for it
  // TODO: Use a function here that filters out the feed from all the units
  const requestParams = JSON.parse(JSON.stringify(cleanedUpRequestParams));

  // TODO: Hack to find array key of feed unit
  const baseFeedUnit = getFeedUnit(requestParams?.units);
  const feedUnitKey = requestParams?.units.indexOf(baseFeedUnit);

  const feedUnit = JSON.parse(JSON.stringify(baseFeedUnit));

  if (feedUnit?.inputFlow?.specificGravity) {
    // TODO: Write better that it's "baseGravity - XX%"
    let specificGravity =
      feedUnit.inputFlow.specificGravity +
      (feedUnit.inputFlow.specificGravity / 100) *
        specificGravityPercentVariance;
    // If it goes below 0, we must make sure we send 0 instead
    if (specificGravity < 0) {
      specificGravity = 0;
    }
    feedUnit.inputFlow.specificGravity = Number(specificGravity.toFixed(2));

    // TODO: Write better that it's "workIndex - XX"
    let workIndex = feedUnit.inputFlow.workIndex + workIndexNumberVariance;
    // If it goes below 0, we must make sure we send 0 instead
    if (workIndex < 0) {
      workIndex = 0;
    }
    feedUnit.inputFlow.workIndex = workIndex;

    // Bulk density also changes when specific gravity is changed
    feedUnit.inputFlow.bulkDensity = parseFloat(
      convertBulkDensity(specificGravity).toFixed(2),
    );

    const minPsd = feedUnit.inputFlow.psd.map((p: number) => {
      if (p === 100) {
        return p;
      }

      p = p + (p / 100) * feedSizeDistributionPercentVariance;

      if (p < 0) {
        return 0;
      }

      return Number(p.toFixed(2));
    });

    feedUnit.inputFlow.psd = minPsd;

    requestParams.units[feedUnitKey] = feedUnit;

    return requestParams;
  }
};

function* performanceguaranteeSaga(action: PGSimulationRequestActionInterface) {
  try {
    const cleanedUpRequestParams: SimulationRequestInterface =
      yield cleanUpRequestParams(action.simulateData);

    // TODO: Inside the function, it always does a "+" operation on variance fields
    // If we specify "-" here - it will subtract them
    const minRequestParams = getRequestParams(
      cleanedUpRequestParams,
      -action.pgVarianceData.specificGravityPercentVariance,
      action.pgVarianceData.workIndexNumberVariance,
      -action.pgVarianceData.feedSizeDistributionPercentVariance,
    );

    const maxRequestParams = getRequestParams(
      cleanedUpRequestParams,
      action.pgVarianceData.specificGravityPercentVariance,
      action.pgVarianceData.workIndexNumberVariance,
      -action.pgVarianceData.feedSizeDistributionPercentVariance,
    );

    const minResult: PromiseReturnType<typeof simulate> = yield withRetry(() =>
      simulate(minRequestParams),
    );

    const maxResult: PromiseReturnType<typeof simulate> = yield withRetry(() =>
      simulate(maxRequestParams),
    );

    yield put(setMinSimulationResult(minResult.parsedBody!)); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    yield put(setMaxSimulationResult(maxResult.parsedBody!)); // eslint-disable-line @typescript-eslint/no-non-null-assertion

    // yield put(setSimulating(false));
  } catch (e) {
    Sentry.captureException(e, {
      tags: { saga: 'performanceGuarantee' },
    });
    console.error(e);
    // yield put(setSimulating(false));
  }
}

function* performanceguaranteeSagas() {
  yield takeLatest(SIMULATE_MIN_MAX, performanceguaranteeSaga);
}

export default performanceguaranteeSagas;
